import React, { useState } from 'react';
import {
  App,
  Button,
  Descriptions,
  Drawer,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { formatDateTime } from '../../utils/format';
import { useDeleteGameServer } from './queries';
import { GameServer, GameServerState } from './types';

const { Text, Paragraph } = Typography;

export const STATE_COLORS: Record<GameServerState, string> = {
  INIT: 'gold',
  READY: 'green',
  PLAYING: 'blue',
  DRAINING: 'purple',
  PLAY_END: 'orange',
  TERMINATED: 'default',
};

const serverTag = (tag?: string) =>
  tag ? (
    <Tag>{tag}</Tag>
  ) : (
    <Tooltip title="Image tag unavailable from Gameye. Historical tags are not stored.">
      <Text type="secondary">—</Text>
    </Tooltip>
  );

export const ServerListTable: React.FC<{
  servers: GameServer[];
  page: number;
  total: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onRooms: (serverID: string) => void;
  onLogs: (serverID: string) => void;
}> = ({ servers, page, total, loading, onPageChange, onRooms, onLogs }) => {
  const { message } = App.useApp();
  const deletion = useDeleteGameServer();
  const [detail, setDetail] = useState<GameServer>();
  const server = servers.find((item) => item.id === detail?.id) ?? detail;

  const handleDelete = async (serverID: string) => {
    try {
      await deletion.mutateAsync(serverID);
      message.success('Server deleted. Pool history is retained.');
      setDetail((current) => (current?.id === serverID ? undefined : current));
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : 'Failed to delete game server.',
      );
    }
  };

  const columns: ColumnsType<GameServer> = [
    {
      title: 'Server ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => (
        <Text code copyable>
          {id}
        </Text>
      ),
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: GameServerState) => (
        <Tag color={STATE_COLORS[state]}>{state}</Tag>
      ),
    },
    { title: 'Image tag', dataIndex: 'tag', key: 'tag', render: serverTag },
    {
      title: 'IP / host',
      dataIndex: 'host',
      key: 'host',
      render: (host: string) => (host ? <Text copyable>{host}</Text> : '—'),
    },
    {
      title: 'Port',
      dataIndex: 'port',
      key: 'port',
      render: (port: number) => port || '—',
    },
    {
      title: 'Tables in use',
      key: 'capacity',
      render: (_, item) =>
        `${item.active_rooms ?? '—'} / ${item.state === 'INIT' ? '—' : (item.capacity ?? '—')}`,
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: formatDateTime,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: formatDateTime,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_, item) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => setDetail(item)}>
            Detail
          </Button>
          <Popconfirm
            title="Delete game server?"
            description={
              <div style={{ maxWidth: 320 }}>
                Stop {item.id}? Connected players will be disconnected.
                Automatic scaling may create a replacement.
              </div>
            }
            okText="Delete server"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(item.id)}
            disabled={
              loading || deletion.isPending || item.state === 'TERMINATED'
            }
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              disabled={
                loading || deletion.isPending || item.state === 'TERMINATED'
              }
              loading={deletion.isPending && deletion.variables === item.id}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="game-server-list">
      <Table<GameServer>
        rowKey="id"
        columns={columns}
        dataSource={servers}
        loading={loading}
        scroll={{ x: 1400 }}
        locale={{
          emptyText: 'No servers found. Try another server ID or state.',
        }}
        pagination={{
          current: page,
          pageSize: 20,
          total,
          showSizeChanger: false,
          disabled: loading,
          onChange: onPageChange,
        }}
      />
      <Paragraph type="secondary">
        Deleting stops the container immediately. Historical records remain
        visible as TERMINATED. Unavailable image tags are shown as —.
      </Paragraph>
      <Drawer
        title="Game server detail"
        width={640}
        open={Boolean(server)}
        onClose={() => setDetail(undefined)}
      >
        {server && (
          <>
            <Descriptions
              bordered
              size="small"
              column={1}
              items={[
                {
                  key: 'id',
                  label: 'Server ID',
                  children: <Text copyable>{server.id}</Text>,
                },
                {
                  key: 'state',
                  label: 'State',
                  children: (
                    <Tag color={STATE_COLORS[server.state]}>{server.state}</Tag>
                  ),
                },
                {
                  key: 'tag',
                  label: 'Image tag',
                  children: serverTag(server.tag),
                },
                {
                  key: 'host',
                  label: 'IP / host',
                  children: server.host ? (
                    <Text copyable>{server.host}</Text>
                  ) : (
                    '—'
                  ),
                },
                { key: 'port', label: 'Port', children: server.port || '—' },
                {
                  key: 'capacity',
                  label: 'Capacity',
                  children:
                    server.state === 'INIT' ? '—' : (server.capacity ?? '—'),
                },
                {
                  key: 'rooms',
                  label: 'Tables in use',
                  children: server.active_rooms ?? '—',
                },
                {
                  key: 'created',
                  label: 'Created',
                  children: formatDateTime(server.created_at),
                },
                {
                  key: 'updated',
                  label: 'Updated',
                  children: formatDateTime(server.updated_at),
                },
                {
                  key: 'source',
                  label: 'Rotation source',
                  children: server.rotation_source_id || '—',
                },
                {
                  key: 'replacement',
                  label: 'Replacement server',
                  children: server.rotation_replacement_id || '—',
                },
              ]}
            />
            <Space className="mt-4">
              <Button
                onClick={() => {
                  onRooms(server.id);
                  setDetail(undefined);
                }}
              >
                View rooms
              </Button>
              <Button onClick={() => onLogs(server.id)}>Logs</Button>
            </Space>
          </>
        )}
      </Drawer>
    </div>
  );
};
